import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { trackError } from "@/utils/error-tracking";
import { AuthProvider, useAuthContext } from "@/components/AuthProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { UpgradeToOwnerPage } from "@/pages/UpgradeToOwnerPage";
import { CenterProvider } from "@/contexts/CenterContext";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { CentersPage } from "@/pages/CentersPage";
import { CenterDetailPage } from "@/pages/CenterDetailPage";
import { InstructorMappingPage } from "@/pages/settings/InstructorMappingPage";
import { InstructorJournalsPage } from "@/pages/InstructorJournalsPage";
import { AdminJournalDetailPage } from "@/pages/AdminJournalDetailPage";
import { AdminMembersListPage } from "@/pages/AdminMembersListPage";
import { AdminMemberDetailPage } from "@/pages/AdminMemberDetailPage";
import { AdminTicketCatalogPage } from "@/pages/AdminTicketCatalogPage";
import { AdminSchedulesPage } from "@/pages/AdminSchedulesPage";
import { BranchPolicySettingsPage } from "@/pages/settings/BranchPolicySettingsPage";
import { AdminSettlementPage } from "@/pages/settlements/AdminSettlementPage";
import { InstructorDetailPage } from "@/pages/InstructorDetailPage";
import { Layout } from "@/components/Layout";
import { usePageTracking } from "@/hooks/usePageTracking";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (error) => {
        trackError(error, { source: "mutation" });
      },
    },
  },
});

function AppRoutes() {
  usePageTracking();
  const auth = useAuthContext();

  // 강사 계정으로 로그인하면 원장 전환 화면을 전역적으로 우선 렌더한다.
  // (전환 성공 후 refresh → authenticated 가 되면 아래 정상 라우트로 복귀)
  if (auth.status === "needs-upgrade") {
    return <UpgradeToOwnerPage pending={auth.pending} />;
  }

  // 인증 해석 중에는 전역 스피너를 렌더한다. needs-upgrade → refresh() 사이의
  // loading 구간에 /login 등 라우트가 잠깐 노출되는 깜빡임을 막는다.
  if (auth.status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <DashboardPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <CentersPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers/:centerId"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <CenterDetailPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers/:centerId/instructors"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <InstructorMappingPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers/:centerId/instructors/:instructorId"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <InstructorDetailPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers/:centerId/instructors/:instructorId/journals"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <InstructorJournalsPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers/:centerId/instructors/:instructorId/journals/:journalId"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <AdminJournalDetailPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers/:centerId/members"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <AdminMembersListPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers/:centerId/members/:memberId"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <AdminMemberDetailPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers/:centerId/ticket-catalog"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <AdminTicketCatalogPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers/:centerId/schedules"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <AdminSchedulesPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers/:centerId/settings"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <BranchPolicySettingsPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/centers/:centerId/settlements"
        element={
          <ProtectedRoute>
            <CenterProvider>
              <Layout>
                <AdminSettlementPage />
              </Layout>
            </CenterProvider>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
